interface ModalSessionData {
    userId: string;
    docId: string;
    collectionPath?: string;
    originalState: {
        completionStatus: boolean;
        isFavorited: boolean;
        notes: string;
    };
    currentState: {
        completionStatus: boolean;
        isFavorited: boolean;
        notes: string;
    };
    timestamp: number;
}

interface ChangeSummary {
    completionStatus?: { from: boolean; to: boolean };
    isFavorited?: { from: boolean; to: boolean };
    notes?: { from: string; to: string };
}

class SessionStorageService {
    private getSessionKey(userId: string, docId: string): string {
        return `modalSession_${userId}_${docId}`;
    }

    // Save or update session data
    saveSession(
        userId: string,
        docId: string,
        collectionPath: string | undefined,
        originalState: any,
        currentState: any
    ): void {
        try {
            const sessionData: ModalSessionData = {
                userId,
                docId,
                collectionPath,
                originalState: {
                    completionStatus: originalState.completionStatus || false,
                    isFavorited: originalState.isFavorited || false,
                    notes: originalState.notes || ""
                },
                currentState: {
                    completionStatus: currentState.completionStatus || false,
                    isFavorited: currentState.isFavorited || false,
                    notes: currentState.notes || ""
                },
                timestamp: Date.now()
            };

            const key = this.getSessionKey(userId, docId);
            sessionStorage.setItem(key, JSON.stringify(sessionData));
        } catch (error) {
            console.error('Error saving session:', error);
            // Fallback: if sessionStorage fails, we'll use immediate saves
        }
    }

    // Get session data
    getSession(userId: string, docId: string): ModalSessionData | null {
        try {
            const key = this.getSessionKey(userId, docId);
            const data = sessionStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error getting session:', error);
            return null;
        }
    }

    // Clear session data
    clearSession(userId: string, docId: string): void {
        try {
            const key = this.getSessionKey(userId, docId);
            sessionStorage.removeItem(key);
        } catch (error) {
            console.error('Error clearing session:', error);
        }
    }

    // Check if session has any changes
    hasChanges(sessionData: ModalSessionData): boolean {
        if (!sessionData) return false;

        const { originalState, currentState } = sessionData;

        return (
            originalState.completionStatus !== currentState.completionStatus ||
            originalState.isFavorited !== currentState.isFavorited ||
            originalState.notes !== currentState.notes
        );
    }

    // Get summary of changes
    getChangeSummary(sessionData: ModalSessionData): ChangeSummary {
        if (!sessionData || !this.hasChanges(sessionData)) {
            return {};
        }

        const { originalState, currentState } = sessionData;
        const changes: ChangeSummary = {};

        if (originalState.completionStatus !== currentState.completionStatus) {
            changes.completionStatus = {
                from: originalState.completionStatus,
                to: currentState.completionStatus
            };
        }

        if (originalState.isFavorited !== currentState.isFavorited) {
            changes.isFavorited = {
                from: originalState.isFavorited,
                to: currentState.isFavorited
            };
        }

        if (originalState.notes !== currentState.notes) {
            changes.notes = {
                from: originalState.notes,
                to: currentState.notes
            };
        }

        return changes;
    }

    // Update current state in session
    updateSessionState(
        userId: string,
        docId: string,
        updates: Partial<{
            completionStatus: boolean;
            isFavorited: boolean;
            notes: string;
        }>
    ): void {
        try {
            const sessionData = this.getSession(userId, docId);
            if (!sessionData) return;

            const updatedSessionData: ModalSessionData = {
                ...sessionData,
                currentState: {
                    ...sessionData.currentState,
                    ...updates
                },
                timestamp: Date.now()
            };

            const key = this.getSessionKey(userId, docId);
            sessionStorage.setItem(key, JSON.stringify(updatedSessionData));
        } catch (error) {
            console.error('Error updating session state:', error);
        }
    }

    // Get formatted change summary for display
    getFormattedChangeSummary(changes: ChangeSummary): string[] {
        const summaries: string[] = [];

        if (changes.completionStatus) {
            const { from, to } = changes.completionStatus;
            summaries.push(`Completion status: ${from ? 'Complete' : 'Incomplete'} → ${to ? 'Complete' : 'Incomplete'}`);
        }

        if (changes.isFavorited) {
            const { from, to } = changes.isFavorited;
            summaries.push(`Favorite status: ${from ? 'Favorited' : 'Not favorited'} → ${to ? 'Favorited' : 'Not favorited'}`);
        }

        if (changes.notes) {
            const { from, to } = changes.notes;
            if (!from && to) {
                summaries.push('Notes: Added new content');
            } else if (from && !to) {
                summaries.push('Notes: Removed content');
            } else {
                summaries.push('Notes: Updated content');
            }
        }

        return summaries;
    }
}

const sessionStorageService = new SessionStorageService();
export default sessionStorageService;
export type { ModalSessionData, ChangeSummary }; 